import { useUpdateUserProfile } from '@/api/queries/userProfile/useUpdateUserProfile';
import AdobeAnalyticsContext from '@/context/AdobeAnalyticsContext';
import { AdobeAnalyticsConst } from '@/hooks/AdobeAnalyticsHooks/adobeAnalyticsConstants/adobeAnalyticsConstants';
import { useLocationInfo, useUserProfile } from '@/hooks';
import Button from '@/storybook/components/Button/Button';
import If from '@/storybook/components/If/If';
import Link from '@/storybook/components/Link/Link';
import Text from '@/storybook/components/Text/Text';
import { useContext, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import styles from './CookieConsentBanner.module.scss';

const cookieNotice = 'https://www.roche.com/cookie-notice';
const adobeAnalyticsOptOut = 'https://www.adobe.com/privacy/opt-out.html';

const CookieConsentBanner = () => {
  const { t } = useTranslation();
  const locationInfo = useLocationInfo();
  const analytics = useContext(AdobeAnalyticsContext);
  const user = useUserProfile();
  const { mutate: updateUserProfile } = useUpdateUserProfile();
  const [isAnimationActive, setIsAnimationActive] = useState(false);
  const showUserConsent = Boolean(user?.cookieConsentBanner);

  useEffect(() => {
    if (showUserConsent) {
      setIsAnimationActive(true);
    }
  }, [showUserConsent, setIsAnimationActive]);

  const onAnimationEnd = () => {
    if (!showUserConsent) {
      setIsAnimationActive(false);
    }
  };

  const acceptCookiesAction = () => {
    const pageIdentifier = AdobeAnalyticsConst.PAGE_IDENTIFIER.COOKIES_CONSENT_BANNER;
    analytics?.trackPageView({ pageIdentifier, locationInfo });
    updateUserProfile({
      cookieConsentBanner: false,
      userId: user.userId,
    });
  };

  return (
    <>
      <If condition={isAnimationActive}>
        <If condition={showUserConsent}>
          <div className={styles.csk_highlighter} />
        </If>
        <div
          onAnimationEnd={onAnimationEnd}
          className={`${styles.cookies_wrap} ${showUserConsent ? styles.slide_top : styles.slide_bottom}`}
        >
          <div className={styles.cookies_wrap_l}>
            <p className={styles.cks_headins}>{t('cookieBanner.ourUseOfCookies')}</p>
            <p className={styles.cks_txt}>{t('cookieBanner.description')}</p>
            <div className={styles.cks_links}>
              <Text as="div">
                <Link to={cookieNotice} isExternal appearance="primary" size="S" data-testid="cookie-notice-link">
                  {t('cookieBanner.links.cookieNotice')}
                </Link>
              </Text>
              <Text as="div">
                <Link
                  to={adobeAnalyticsOptOut}
                  isExternal
                  appearance="primary"
                  size="S"
                  data-testid="adobe-analytics-optout-link"
                >
                  {t('cookieBanner.links.adobeAnalyticsOptOut')}
                </Link>
              </Text>
            </div>
          </div>
          <div className={styles.cookies_wrap_r} onClick={acceptCookiesAction}>
            <Button size="L">{t('cookieBanner.acknowledge')}</Button>
          </div>
        </div>
      </If>
    </>
  );
};

export default CookieConsentBanner;
